/** Exact structure work shares the standalone controller and its drafts. */
import { useAuth } from "@/contexts/AuthContext";
import { useLocation } from "react-router";
import type { VisitLevel } from "@/lib/visit/address";
import type { VisitResolution } from "@/lib/visit/registry";
import { useStructureModels, type StructureKind } from "./use-structure-models";
import { structureCopy as words } from "./structure-copy";

export const STRUCTURE_OBJECT: Record<StructureKind, string> = { department: "department", program: "program", cohort: "cohort", semester: "term" };
const kindOf = (object: string): StructureKind | undefined => (Object.keys(STRUCTURE_OBJECT) as StructureKind[]).find(kind => STRUCTURE_OBJECT[kind] === object);

export function useStructureVisit(level: VisitLevel | null): VisitResolution | null {
  const { me } = useAuth();
  const { pathname } = useLocation();
  const kind = level ? kindOf(level.object) : undefined;
  const allowed = me?.role === "admin";
  const creating = level?.job === "create" || level?.job === "offer-create";
  const models = useStructureModels({ enabled: allowed && !!kind, kind, origin: creating && level?.id !== "new" ? `${level!.id}${level?.job === "offer-create" ? "/offer" : ""}` : null, reviewTerm: level?.object === "term" && level.job === "review" ? level.id : undefined });
  if (!level) return null;
  if (!me) return { status: "loading" };
  if (!allowed || !kind || models.refused) return { status: "refused" };
  const query = models.queries[kind];
  const returnLabel = pathname.startsWith("/admin/courses") ? level.job === "offer-create" ? "Back to Offer again" : "Back to course setup" : `Back to ${words.kinds[kind].plural}`;
  if (creating) return {
    status: "ready", subject: `New ${kind === "semester" ? "term" : kind}`, returnLabel,
    boxes: [{ ...models.creates[kind], fold: "never", footer: "host" }], opens: [`new-${kind}`],
  };
  const row = models.findRecord(kind, level.id);
  if (query.isPending) return { status: "loading" };
  if (!query.isError && !row) return { status: "refused" };
  return { status: "ready", subject: row ? `${row.name} · ${kind === "semester" ? "Term" : kind[0].toUpperCase() + kind.slice(1)}` : kind === "semester" ? "Term" : kind, returnLabel,
    boxes: models.record(kind, level.id), opens: [`structure-record-${kind}-${level.id}`, ...(kind === "semester" && level.job === "review" ? [`structure-status-${kind}-${level.id}`] : [])] };
}
